import { SGeneralForms, SLocalTreeSelected } from 'shj-components'
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Button, Input, message, Select, TreeSelect } from 'antd'
import {getEnums, httpOrgList} from '@/services/homeService.ts'
import { getProductCommonPage } from '@/services/product.ts'
import { debounce, isEmpty } from 'lodash-es'
import { useSetState } from 'ahooks'
import { replaceRule, trimStr } from '@/utils/data.ts'
import DebounceSearchSelect from '@/pages/baseProduct/components/DebounceSearchSelect'
import dayjs from 'dayjs'
import DebounceSelect from '@/pages/baseProduct/components/DebounceSearchSelect'
import { getRolePage } from '@/services/homeService.ts'

type IProps = {
  record: Record<string, any>
  chooseTypeKey?: string
  genItemId?: string
  fn: (data: Record<string, any>) => void
  setLoading: (flag: boolean) => void
  readOnly?: boolean
}

export const AddOrEditStandard = forwardRef((props: IProps, ref) => {
  const formRef = useRef()

  const [state, setState] = useSetState<any>({
    initValues: {},
    companyList: [],
    productList: [],
    discountTypeList: [],
    discountSubTypeList: [],
    budgetRule: '',
    searchValue: '',
  })

  const [ruleModal, setRuleModal] = useSetState({
    open: false,
    mode: 'modal',
    value: '',
  })

  const onFormRef = () => {
    const { onFormRef } = formRef.current || {}
    return onFormRef?.()
  }

  useEffect(() => {
    getOptions()
    if (!isEmpty(props.record)) {
      const obj = {
        ...props.record,
        startDate: dayjs(props.record.startDate),
        endDate: props.record.endDate && dayjs(props.record.endDate),
      }
      onFormRef()?.setFieldsValue(obj)
    }
  }, [props.record])

  const getOptions = async () => {
    const companyList: any = await httpOrgList()
    const discountTypeList = await getEnums('plus-product', 'com.shj.saas.plus.product.enums.DiscountTypeEnum')
    const discountSubTypeList = await getEnums('plus-product', 'com.shj.saas.plus.product.enums.DiscountSubcategoryTypeEnum')
    setState({ companyList, discountTypeList, discountSubTypeList })
  }

  const onFinish = () => {
    onFormRef()
      ?.validateFields()
      .then(values => {
        props.setLoading?.(true)
        const values_ = {
          ...values,
          startDate: values.startDate && dayjs(values.startDate).format('YYYY-MM-DD'),
          endDate: values.endDate && dayjs(values.endDate).format('YYYY-MM-DD'),
        }
        if (props.genItemId) {
          props.fn?.(values_)
        } else {
          props.fn?.({ ...values_, key: values.key || +new Date() })
          props.setLoading?.(false)
        }
      })
  }

  useImperativeHandle(ref, () => {
    return {
      onFinish,
    }
  }, [onFinish])

  return (
    <>
      <SGeneralForms
        ref={formRef}
        isHideGobackBtn={true}
        layout={'vertical'}
        initialValues={{
          productIdList:['0'],
          orgIdList:['0'],
          discountSubcategoryType: 'PROJECT_TOTAL_DISCOUNT',
        }}
        configs={[
          {
            formItem: [
              { name: 'id', hidden: true },
              {
                name: props.record.id ? 'productId' : 'productIdList',
                label: '适用产品',
                required: true,
                hidden: props.record.id,
                rules: [{ required: true, message: '适用产品不能为空' }],
                spanCol: 24,
                children: (
                  <DebounceSelect
                    allowClear
                    label={'产品'}
                    isCommon={true}
                    mode={'multiple'}
                    fixedParams={{ prodTypes: ["PRODUCT_PACKAGE"] }}
                    fetchOptions={getProductCommonPage}
                    fieldNames={{ label: 'prodName', value: 'id' }}
                  />
                ),
              },
              {
                spanCol: 24,
                label: '适用门店',
                name: props.record.id ? 'orgId' : 'orgIdList',
                required: true,
                hidden: props.record.id,
                rules: [{ required: true, message: '适用门店不能为空' }],
                children: (
                  <SLocalTreeSelected
                    treeCheckable
                    disabled={props.record?.id}
                    treeDefaultExpandAll
                    showCheckedStrategy={TreeSelect.SHOW_PARENT}
                    fieldNames={{ label: 'name', value: 'id' }}
                    treeData={[{ name: '通用', id: '0' }, ...state.companyList]}
                  />
                ),
              },
              {
                name: 'roleId',
                label: '适用角色',
                required: true,
                hidden: props.record.id,
                rules: [{ required: true, message: '适用角色不能为空' }],
                spanCol: 24,
                children: (
                  <DebounceSelect
                    allowClear
                    label={'角色'}
                    nameKey="nameKeyword"
                    fetchOptions={getRolePage}
                    fieldNames={{ label: 'name', value: 'id' }}
                  />
                ),
              },
              {
                name: 'discountType',
                label: '优惠大类',
                required: true,
                hidden: props.record.id,
                rules: [{ required: true, message: '优惠大类不能为空' }],
                spanCol: 24,
                children: (
                  <Select
                      placeholder={'请选择优惠大类'}
                      options={state.discountTypeList}
                    allowClear
                    label={'优惠大类'}
                    fieldNames={{ label: 'description', value: 'key' }}
                  />
                ),
              },
              {
                name: 'discountSubcategoryType',
                label: '优惠小类',
                required: true,
                hidden: props.record.id,
                rules: [{ required: true, message: '优惠小类不能为空' }],
                spanCol: 24,
                children: (
                  <Select
                      placeholder={'请选择优惠小类'}
                      options={state.discountSubTypeList}
                    allowClear
                    label={'优惠小类'}
                    fieldNames={{ label: 'description', value: 'key' }}
                  />
                ),
              },
              {
                spanCol: 24,
                label: '值',
                name: 'ratioVal',
                required: true,
                rules: [{ required: true, message: '值不能为空' }],
                type: 'inputNumber',
                comConfig: {
                  precision: 4,
                  min: 0.0001,
                  max: 1,
                  placeholder: '请输入不大于1的正数，例如0.8代表最多可打8折',
                },
              },
              {
                spanCol: 24,
                label: '开始日期',
                name: 'startDate',
                required: true,
                rules: [{ required: true, message: '开始日期不能为空' }],
                type: 'date',
              },
              {
                spanCol: 24,
                label: '结束日期',
                name: 'endDate',
                type: 'date',
                comConfig: {
                  disabledDate: current => {
                    const startDate = onFormRef().getFieldValue('startDate')
                    if (startDate) return current && current < dayjs(startDate).endOf('day')
                  },
                },
              },
            ],
          },
        ]}
      />
    </>
  )
})
